import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Euro, 
  ShoppingCart, 
  TrendingUp, 
  Target, 
  Megaphone, 
  CreditCard,
  ArrowUp,
  ArrowDown,
  Minus,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { MOCK_METRICS, generateTimeSeriesData } from '@/lib/adapters/mockData';
import type { FilterState } from './GlobalFilters';

interface KpiCardsProps {
  clientId: string;
  filters: FilterState;
}

interface KpiCardData {
  title: string;
  value: string | number;
  delta: number;
  unit?: string;
  icon: React.ElementType;
  color: string;
  description: string;
  sparklineData?: number[];
}

export function KpiCards({ clientId, filters }: KpiCardsProps) {
  const [kpiData, setKpiData] = useState<KpiCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDemoData, setIsDemoData] = useState(true);
  const [credentialsStatus, setCredentialsStatus] = useState<{
    sp: string;
    ads: string;
    wallet: string;
  } | null>(null);

  useEffect(() => {
    fetchMetrics();
  }, [clientId, filters]);

  const fetchMetrics = async () => {
    setLoading(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('No session found');
        loadMockData();
        return;
      }

      const response = await supabase.functions.invoke('fetch-amazon-metrics', {
        body: {
          clientId,
          from: filters.dateRange.from.toISOString(),
          to: filters.dateRange.to.toISOString(),
          marketplace: filters.marketplace === 'all' ? undefined : filters.marketplace
        }
      });

      if (response.error) {
        console.error('Error fetching metrics:', response.error);
        loadMockData();
        return;
      }

      const { demo, data: metricsData, credentials_status } = response.data;
      
      setIsDemoData(demo);
      setCredentialsStatus(credentials_status);

      if (demo || !metricsData) {
        loadMockData();
        return;
      }

      // Utiliser les vraies données Amazon
      buildKpiCards(metricsData);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = () => {
    const timeSeriesData = generateTimeSeriesData(30);
    const currentPeriod = timeSeriesData.slice(-15);
    const previousPeriod = timeSeriesData.slice(-30, -15);

    const calculateDelta = (current: number[], previous: number[]): number => {
      const currentSum = current.reduce((sum, val) => sum + val, 0);
      const previousSum = previous.reduce((sum, val) => sum + val, 0);
      return previousSum > 0 ? ((currentSum - previousSum) / previousSum) * 100 : 0;
    };

    const cards: KpiCardData[] = [
      {
        title: 'Chiffre d\'affaires',
        value: MOCK_METRICS.revenue.toLocaleString('fr-FR'),
        unit: '€',
        delta: calculateDelta(
          currentPeriod.map(d => d.revenue),
          previousPeriod.map(d => d.revenue)
        ),
        icon: Euro,
        color: 'text-green-600',
        description: '30 derniers jours',
        sparklineData: timeSeriesData.map(d => d.revenue),
      },
      {
        title: 'Commandes',
        value: MOCK_METRICS.orders.toLocaleString('fr-FR'),
        delta: calculateDelta(
          currentPeriod.map(d => d.orders),
          previousPeriod.map(d => d.orders)
        ),
        icon: ShoppingCart,
        color: 'text-blue-600',
        description: 'Total des commandes',
        sparklineData: timeSeriesData.map(d => d.orders),
      },
      {
        title: 'Taux de conversion',
        value: (MOCK_METRICS.conversionRate * 100).toFixed(2),
        unit: '%',
        delta: calculateDelta(
          currentPeriod.map(d => d.conversion * 100),
          previousPeriod.map(d => d.conversion * 100)
        ),
        icon: TrendingUp,
        color: 'text-purple-600',
        description: 'Conversion visiteurs → commandes',
        sparklineData: timeSeriesData.map(d => d.conversion * 100),
      },
      {
        title: 'Part Buy Box',
        value: (MOCK_METRICS.buyBox.share * 100).toFixed(1),
        unit: '%',
        delta: calculateDelta(
          currentPeriod.map(d => d.buyBoxShare * 100),
          previousPeriod.map(d => d.buyBoxShare * 100)
        ),
        icon: Target,
        color: 'text-orange-600',
        description: '% temps Buy Box détenue',
        sparklineData: timeSeriesData.map(d => d.buyBoxShare * 100),
      },
      {
        title: 'ACoS / ROAS',
        value: `${(MOCK_METRICS.ads.acos * 100).toFixed(1)}% / ${MOCK_METRICS.ads.roas.toFixed(1)}`,
        delta: calculateDelta(
          currentPeriod.map(d => d.acos * 100),
          previousPeriod.map(d => d.acos * 100)
        ),
        icon: Megaphone,
        color: 'text-red-600',
        description: 'Performance publicitaire',
        sparklineData: timeSeriesData.map(d => d.acos * 100),
      },
      {
        title: 'Frais Amazon',
        value: MOCK_METRICS.amazonFees.total.toLocaleString('fr-FR'),
        unit: '€',
        delta: -8.5,
        icon: CreditCard,
        color: 'text-yellow-600',
        description: 'Commissions + FBA + Ads',
      },
    ];

    setKpiData(cards);
    setIsDemoData(true);
  };

  const buildKpiCards = (metricsData: any) => {
    const cards: KpiCardData[] = [
      {
        title: 'Chiffre d\'affaires',
        value: metricsData.revenue.toLocaleString('fr-FR'),
        unit: '€',
        delta: 12.5, // TODO: Calculer la vraie variation
        icon: Euro,
        color: 'text-green-600',
        description: filters.dateRange.from && filters.dateRange.to 
          ? `Du ${filters.dateRange.from.toLocaleDateString('fr-FR')} au ${filters.dateRange.to.toLocaleDateString('fr-FR')}`
          : '30 derniers jours',
      },
      {
        title: 'Commandes',
        value: metricsData.orders.toLocaleString('fr-FR'),
        delta: 8.3,
        icon: ShoppingCart,
        color: 'text-blue-600',
        description: 'Total des commandes',
      },
      {
        title: 'Taux de conversion',
        value: metricsData.conversionRate.toFixed(2),
        unit: '%',
        delta: 5.2,
        icon: TrendingUp,
        color: 'text-purple-600',
        description: 'Conversion visiteurs → commandes',
      },
      {
        title: 'Part Buy Box',
        value: metricsData.buyBoxShare.toFixed(1),
        unit: '%',
        delta: 3.1,
        icon: Target,
        color: 'text-orange-600',
        description: '% temps Buy Box détenue',
      },
      {
        title: 'ACoS / ROAS',
        value: `${metricsData.acos.toFixed(1)}% / ${metricsData.roas.toFixed(1)}`,
        delta: -2.4,
        icon: Megaphone,
        color: 'text-red-600',
        description: 'Performance publicitaire',
      },
      {
        title: 'Frais Amazon',
        value: (Object.values(metricsData.fees) as number[]).reduce((sum, val) => sum + val, 0).toLocaleString('fr-FR'),
        unit: '€',
        delta: -8.5,
        icon: CreditCard,
        color: 'text-yellow-600',
        description: 'Commissions + FBA + Ads',
      },
    ];

    setKpiData(cards);
  };

  const getDeltaIcon = (delta: number) => {
    if (delta > 0) return <ArrowUp className="w-3 h-3" />;
    if (delta < 0) return <ArrowDown className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  const getDeltaColor = (delta: number, isInverted = false) => {
    if (delta > 0) return isInverted ? 'text-red-600' : 'text-green-600';
    if (delta < 0) return isInverted ? 'text-green-600' : 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-gray-200 rounded w-20"></div>
              <div className="h-4 w-4 bg-gray-200 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <>
      {isDemoData && (
        <Alert className="mb-4 bg-orange-50 border-orange-200">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            Vous visualisez des données de démonstration. 
            {credentialsStatus?.sp === 'missing' && credentialsStatus?.ads === 'missing' 
              ? ' Configurez vos API Amazon dans les paramètres pour voir vos vraies données.'
              : ' Connexion aux APIs Amazon en cours...'}
          </AlertDescription>
        </Alert>
      )}
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {kpiData.map((kpi, index) => {
          const Icon = kpi.icon;
          const isInverted = kpi.title.includes('ACoS') || kpi.title.includes('Frais');
          
          return (
            <Card key={index} className="group hover:shadow-lg transition-all duration-200">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {kpi.title}
                </CardTitle>
                <Icon className={`h-4 w-4 ${kpi.color}`} />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-baseline space-x-2">
                    <div className="text-2xl font-bold">
                      {kpi.value}
                      {kpi.unit && <span className="text-lg text-muted-foreground ml-1">{kpi.unit}</span>}
                    </div>
                    {Math.abs(kpi.delta) > 0 && (
                      <Badge
                        variant="secondary"
                        className={`${getDeltaColor(kpi.delta, isInverted)} border-0 text-xs`}
                      >
                        {getDeltaIcon(kpi.delta)}
                        {Math.abs(kpi.delta).toFixed(1)}%
                      </Badge>
                    )}
                  </div>
                  
                  <CardDescription className="text-xs">
                    {kpi.description}
                  </CardDescription>

                  {/* Mini sparkline simulation */}
                  {kpi.sparklineData && (
                    <div className="h-8 flex items-end space-x-0.5 opacity-50 group-hover:opacity-100 transition-opacity">
                      {kpi.sparklineData.slice(-14).map((value, i) => {
                        const max = Math.max(...kpi.sparklineData!);
                        const min = Math.min(...kpi.sparklineData!);
                        const height = ((value - min) / (max - min)) * 24 + 4;
                        return (
                          <div
                            key={i}
                            className={`w-1 ${kpi.color.replace('text-', 'bg-')} rounded-sm opacity-70`}
                            style={{ height: `${height}px` }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}